<script setup lang="ts">
import autoAnimate from '@formkit/auto-animate';
import { computed, onMounted, ref } from 'vue';
import { useCartSummary } from '../../composables/useCartSummary';
import { formatCurrency } from '../../utils/currencyHelpers';
import { formatWithoutRounding } from '../../utils/productPrice';
import { useCheckoutStateStore } from '../checkout/stores/checkoutStateStore';
import FeedbackMessage from './FeedbackMessage.vue';
import Icon from '../icon/icon.vue';
import { useShoppingCartStore } from './stores/shoppingCartStore';
import { useOrderTypeStore } from './stores/orderTypeStore';
import { useClickAndCollectReservationStore } from './stores/clickAndCollectReservationStore';

const props = defineProps<{
  showCheckoutLink?: boolean;
  error?: string;
  // Owned by the parent (`shoppingCart.vue`): it calls `verifyStock()` and
  // reacts to the result (navigate, submit the click & collect form, focus
  // `StockSummary` on failure). This component only renders the buttons and
  // their busy state, calling straight through to whichever callback the
  // parent supplied — no emit needed, and no default assumes a parent is
  // listening (this component is also mounted standalone, without either
  // button shown, on the checkout-step page).
  onGoToCheckout?: () => Promise<void>;
  onConfirmClickAndCollect?: () => Promise<void>;
}>();

const shoppingCartStore = useShoppingCartStore();
const checkoutStateStore = useCheckoutStateStore();
const orderTypeStore = useOrderTypeStore();
const reservationStore = useClickAndCollectReservationStore();
const { shippingCostFormatted, totalAmount } = useCartSummary();
const lineItemsList = ref();
const totalList = ref();
const reserveError = ref();
const isVerifyingStock = ref(false);

const error = computed(() => {
  return props.error;
});

const isClickAndCollectActive = computed(() => {
  return checkoutStateStore.clickAndCollectEnabled && orderTypeStore.isClickAndCollect;
});

const showCheckoutButton = computed(
  () => !!props.showCheckoutLink && shoppingCartStore.hasItemsInCart && !isClickAndCollectActive.value
);

const showReserveButton = computed(
  () => !!props.showCheckoutLink && shoppingCartStore.hasItemsInCart && isClickAndCollectActive.value
);

const handleGoToCheckout = async () => {
  isVerifyingStock.value = true;
  try {
    await props.onGoToCheckout?.();
  } finally {
    isVerifyingStock.value = false;
  }
};

const handleConfirmClickAndCollect = async () => {
  isVerifyingStock.value = true;
  try {
    await props.onConfirmClickAndCollect?.();
  } finally {
    isVerifyingStock.value = false;
  }
};

onMounted(() => {
  autoAnimate(lineItemsList.value);
  autoAnimate(totalList.value);
  autoAnimate(reserveError.value);
});
</script>

<template>
  <div class="checkout-summary">
    <h2 class="checkout-summary__heading">{{ $t('summary') }}</h2>
    <FeedbackMessage :message="error" type="error" />
    <div>
      <ul
        v-if="shoppingCartStore.cart?.lineItems"
        ref="lineItemsList"
        class="checkout-summary__group"
      >
        <li
          v-for="lineItem in shoppingCartStore.cart.lineItems"
          :key="lineItem.code"
          class="checkout-summary__entry"
        >
          <div class="checkout-summary__product">
            <span class="checkout-summary__product-name">
              {{ lineItem.name }} ({{ lineItem.quantity }} stk)
            </span>
          </div>
          <div class="checkout-summary__price">
            <span class="checkout-summary__price"
              >{{ formatCurrency(lineItem.listPrice.currency) }}
              {{ formatWithoutRounding(lineItem.quantity * lineItem.discountedPrice.amount) }}
            </span>
          </div>
        </li>
      </ul>
      <div ref="totalList" class="checkout-summary__group">
        <div
          v-if="shoppingCartStore.cart?.discountTotal?.amount"
          class="checkout-summary__entry checkout-summary__entry"
        >
          <div>{{ $t('discountTotal') }}</div>
          <div>
            <span class="checkout-summary__discount"
              >-
              {{
                shoppingCartStore.cart.discountTotal &&
                formatCurrency(shoppingCartStore.cart.discountTotal.currency)
              }}
              {{ formatWithoutRounding(shoppingCartStore.cart.discountTotal.amount) }}</span
            >
          </div>
        </div>
        <div
          v-show="shoppingCartStore.cart?.total?.amount && !isClickAndCollectActive"
          class="checkout-summary__entry checkout-summary__entry"
        >
          <div>{{ $t('shipping') }}</div>
          <div>
            <span>{{ shippingCostFormatted }}</span>
          </div>
        </div>
        <div
          v-if="shoppingCartStore.cart && !shoppingCartStore.freeShipping"
          class="checkout-summary__entry checkout-summary__entry--gray"
        >
          <div class="checkout-summary__shipping-tip">
            {{ shoppingCartStore.shippingCallToActionText }}
          </div>
        </div>
      </div>
      <h4 class="checkout-summary__total">
        <span>{{ $t('total') }}: </span>
        <span
          >{{
            shoppingCartStore.cart?.total && formatCurrency(shoppingCartStore.cart.total.currency)
          }}
          {{ shoppingCartStore.cart?.total && formatWithoutRounding(totalAmount) }}</span
        >
      </h4>
      <!-- Click and Collect payment info -->
      <div v-if="isClickAndCollectActive" class="checkout-summary__click-collect-info">
        <span>Klikk og hent betales i butikk</span>
      </div>
      <div
        v-if="showCheckoutButton || shoppingCartStore.cart?.paymentMethods || isClickAndCollectActive"
        class="checkout-summary__extra"
      >
        <button
          v-if="showCheckoutButton"
          :disabled="isVerifyingStock"
          class="btn btn--profile btn--spinner btn--smaller"
          @click="handleGoToCheckout"
        >
          <Icon v-if="isVerifyingStock" type="spinner" size="tiny" class="icon--spinner"></Icon>
          <span>{{ $t('toCheckout') }}</span>
        </button>
        <button
          v-if="showReserveButton"
          :disabled="isVerifyingStock"
          class="btn btn--profile btn--spinner btn--checkout checkout-form__submit"
          @click="handleConfirmClickAndCollect"
        >
          <Icon v-if="isVerifyingStock" type="spinner" size="tiny" class="icon--spinner"></Icon>
          <span>Bekreft ordre</span>
        </button>
        <div ref="reserveError">
          <FeedbackMessage
            v-if="showReserveButton"
            class="checkout-summary__reserve-error"
            :message="reservationStore.errorMessage"
            type="error"
          />
        </div>
        <div v-if="shoppingCartStore.cart?.paymentMethods" class="checkout-summary__extra-info">
          <span v-if="!isClickAndCollectActive">{{ $t('acceptedPaymentMethods') }}</span>
          <div class="layout-new-checkout__summary-fixed-payment-methods">
            <img
              v-for="logo in shoppingCartStore.paymentLogos"
              :key="logo.id"
              :src="logo.url"
              :alt="logo.alt"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
